import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/feature/SeoHead';
import JsonLd from '@/components/feature/JsonLd';
import PageHero from '@/components/feature/PageHero';
import { taskRules } from '@/mocks/site';

const tocItems = [
  { id: 'reward', label: '奖励发放说明', icon: 'ri-hand-coin-line' },
  { id: 'task', label: '任务规则明细', icon: 'ri-list-check-2' },
  { id: 'lucky', label: '抽奖概率公示', icon: 'ri-trophy-line' },
  { id: 'claim', label: '领取流程', icon: 'ri-route-line' },
  { id: 'safe', label: '常见误区与安全提示', icon: 'ri-shield-check-line' },
];

const probability = [
  { name: '瓜豆 x10', level: '基础', rate: '32%', stock: '不限量' },
  { name: '瓜豆 x20', level: '基础', rate: '25%', stock: '不限量' },
  { name: '瓜豆 x50', level: '基础', rate: '18%', stock: '不限量' },
  { name: '瓜豆 x100', level: '精选', rate: '10%', stock: '每日 500 份' },
  { name: '免单券', level: '精选', rate: '6%', stock: '每日 300 份' },
  { name: '体验会员', level: '精选', rate: '5%', stock: '每日 200 份' },
  { name: '限定周边', level: '稀有', rate: '2%', stock: '每周 50 份' },
  { name: '谢谢参与', level: '参与', rate: '2%', stock: '不限量' },
];

const claimSteps = [
  { title: '确认任务状态', desc: '在「我的福利」里确认任务是否已完成，若显示「待审核」请耐心等待审核结果。' },
  { title: '提交领取申请', desc: '达到奖励门槛后点击领取，涉及实物的奖项需要补充收货信息。' },
  { title: '等待发放到账', desc: '瓜豆类奖励按活动说明的时效到账，实物类奖励在信息确认后进入发货流程。' },
  { title: '查看与使用', desc: '到账后可在瓜豆明细中查询，兑换专区与抽奖入口均会实时同步可用余额。' },
];

const misconceptions = [
  { wrong: '任务做完了奖励一定会马上到账', right: '涉及人工审核的任务需要处理时间，状态会先显示「待发放」，属正常现象。' },
  { wrong: '瓜豆放着不用也没关系', right: '瓜豆自获得之日起 12 个月内有效，临近过期会有提醒，建议及时使用。' },
  { wrong: '中奖后收货信息可以随时改', right: '收货信息提交后不可自行修改，填写前请务必核对电话与地址。' },
  { wrong: '多开几个账号能加速攒奖励', right: '同一设备的多账号异常行为会被识别并回收奖励，正常参与即可。' },
];

export default function Rules() {
  const [activeId, setActiveId] = useState('reward');

  useEffect(() => {
    const sections = tocItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: '玩法与规则 - 爆瓜福利任务奖励规则说明',
        description:
          '详细说明爆瓜福利的任务类型、奖励发放时间、抽奖概率公示与领取流程，并整理常见误区与安全提示。',
        url: 'https://ikinxod.cn/rules',
        inLanguage: 'zh-CN',
        dateModified: new Date().toISOString().slice(0, 10),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ikinxod.cn/' },
          { '@type': 'ListItem', position: 2, name: '玩法与规则', item: 'https://ikinxod.cn/rules' },
        ],
      },
    ],
  };

  return (
    <>
      <SeoHead
        path="/rules"
        title="玩法与规则 - 爆瓜福利任务奖励领取规则说明"
        description="爆瓜福利玩法与规则页详细说明任务类型、奖励发放时间、抽奖概率公示与领取流程，并整理常见误区与安全提示，帮助你在参与前把规则看清楚。"
        keywords="爆瓜福利规则,任务奖励规则,抽奖概率公示,福利领取流程,奖励发放时间"
      />
      <JsonLd data={structuredData} />

      <Layout>
        <PageHero
          icon="ri-book-open-line"
          eyebrow="玩法与规则"
          title="把规则说明白，才敢放心参与"
          desc="这一页集中回答三个问题：任务怎么做、奖励什么时候到、抽奖概率是多少。参与任何活动之前，花几分钟读完基本就不会踩坑。"
          crumbs={[{ label: '首页', path: '/' }, { label: '玩法与规则' }]}
        />

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <aside className="lg:col-span-3">
              <nav aria-label="页面目录" className="lg:sticky lg:top-28 bg-background-100 border border-background-200 rounded-2xl p-5">
                <h2 className="text-sm font-medium text-foreground-950">本页目录</h2>
                <ul className="mt-4 flex flex-col gap-1">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors cursor-pointer ${
                          activeId === item.id
                            ? 'bg-primary-100 text-primary-800 font-medium'
                            : 'text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={item.icon}></i>
                        </span>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/activities"
                  className="mt-5 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-gift-line"></i>
                  去挑活动
                </Link>
              </nav>
            </aside>

            <div className="lg:col-span-9 flex flex-col gap-10 md:gap-14">
              <section id="reward" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl text-foreground-950">奖励发放说明</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  爆瓜福利的奖励分为两类：一类是以瓜豆为单位的虚拟奖励，用于抽奖与兑换；另一类是实物或券类奖励，需要补充收货信息。两类奖励的发放节奏不同，理解了这个差别，基本就不会因为「怎么还没到」而焦虑。
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-background-100 border border-background-200 rounded-2xl p-5">
                    <span className="w-10 h-10 rounded-xl bg-secondary-100 text-secondary-800 flex items-center justify-center text-xl">
                      <i className="ri-flashlight-line"></i>
                    </span>
                    <h3 className="mt-4 text-base text-foreground-950">即时到账</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">签到、浏览等无需审核的任务，完成后立即发放瓜豆。</p>
                  </div>
                  <div className="bg-background-100 border border-background-200 rounded-2xl p-5">
                    <span className="w-10 h-10 rounded-xl bg-accent-100 text-accent-800 flex items-center justify-center text-xl">
                      <i className="ri-hourglass-line"></i>
                    </span>
                    <h3 className="mt-4 text-base text-foreground-950">审核后发放</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">投稿与精选评论需人工审核，一般在采纳后 24 小时内到账。</p>
                  </div>
                  <div className="bg-background-100 border border-background-200 rounded-2xl p-5">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                      <i className="ri-truck-line"></i>
                    </span>
                    <h3 className="mt-4 text-base text-foreground-950">发货类奖励</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">实物奖励在收货信息确认后 10 个工作日内发出，偏远地区顺延。</p>
                  </div>
                </div>
              </section>

              <section id="task" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl text-foreground-950">任务规则明细</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  下表整理了目前最常见的任务类型及其奖励标准。不同活动的数值可能略有差异，参与前请以活动详情页的最新说明为准。
                </p>
                <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left border-collapse">
                      <thead>
                        <tr className="bg-background-200/60 text-foreground-700 text-sm">
                          <th scope="col" className="px-5 py-4 font-medium whitespace-nowrap">任务类型</th>
                          <th scope="col" className="px-5 py-4 font-medium">完成条件</th>
                          <th scope="col" className="px-5 py-4 font-medium whitespace-nowrap">奖励</th>
                          <th scope="col" className="px-5 py-4 font-medium whitespace-nowrap">次数限制</th>
                          <th scope="col" className="px-5 py-4 font-medium whitespace-nowrap">到账时间</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskRules.map((rule) => (
                          <tr key={rule.name} className="border-t border-background-200">
                            <td className="px-5 py-4 text-sm font-medium text-foreground-950 whitespace-nowrap">{rule.name}</td>
                            <td className="px-5 py-4 text-sm text-foreground-600">{rule.require}</td>
                            <td className="px-5 py-4 text-sm text-primary-700 font-medium whitespace-nowrap">{rule.reward}</td>
                            <td className="px-5 py-4 text-sm text-foreground-600 whitespace-nowrap">{rule.limit}</td>
                            <td className="px-5 py-4 text-sm text-foreground-600 whitespace-nowrap">{rule.time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <section id="lucky" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl text-foreground-950">抽奖概率公示</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  每次抽奖由系统在完整奖池中随机产生结果，每次抽取相互独立。下表为当前奖池的奖品构成与中奖概率，活动期间如有调整会同步更新本页。
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {probability.map((item) => (
                    <div
                      key={item.name}
                      className="bg-background-100 border border-background-200 rounded-2xl p-5 flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-950 truncate">{item.name}</p>
                        <p className="mt-1 text-xs text-foreground-500">库存：{item.stock}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg text-primary-700 font-heading">{item.rate}</p>
                        <p className="text-xs text-foreground-500">{item.level}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
                  概率合计约 100%，因四舍五入可能存在 0.1% 以内的显示误差。奖品数量有限，先到先得，抽完即止。
                </p>
              </section>

              <section id="claim" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl text-foreground-950">领取流程</h2>
                <div className="mt-6 flex flex-col gap-4">
                  {claimSteps.map((step, index) => (
                    <div key={step.title} className="flex items-start gap-4 bg-background-100/70 border border-background-200 rounded-2xl p-5">
                      <span className="shrink-0 w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg">
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="text-base text-foreground-950">{step.title}</h3>
                        <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="safe" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl text-foreground-950">常见误区与安全提示</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  很多疑问其实来自对规则的误解。下面把最常见的四个误区与正确理解放在一起对比，看完可以省下不少来回咨询的时间。
                </p>
                <div className="mt-6 flex flex-col gap-4">
                  {misconceptions.map((item) => (
                    <div key={item.wrong} className="bg-background-100 border border-background-200 rounded-2xl p-5">
                      <p className="flex items-start gap-3 text-sm text-foreground-500">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs">
                          <i className="ri-close-line"></i>
                        </span>
                        误区：{item.wrong}
                      </p>
                      <p className="mt-3 flex items-start gap-3 text-sm text-foreground-800">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-xs">
                          <i className="ri-check-line"></i>
                        </span>
                        正确理解：{item.right}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 bg-primary-50 border border-primary-200 rounded-2xl p-5 md:p-6">
                  <h3 className="flex items-center gap-2 text-base text-primary-900">
                    <i className="ri-shield-check-line"></i>
                    安全提示
                  </h3>
                  <p className="mt-2 text-sm text-primary-900/80 leading-relaxed">
                    本站活动全部免费参与，工作人员不会主动索要验证码、密码或要求转账。若遇到冒充客服的可疑请求，请勿提供任何账号信息，并通过反馈邮箱与我们核实。
                  </p>
                </div>
              </section>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
}